import React, { useEffect, useState } from "react";
import useLoyalty from "@/hooks/useLoyalty";
import {
  UserPlus, ChevronDown, ChevronUp, Coins, Award, Info, Clock,
  TrendingUp, Gift, AlertTriangle, CheckCircle, Calculator, XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";

/* ---------------- Collapsible Section ---------------- */
const Section = ({ title, subtitle, icon: Icon, children, defaultOpen = false, badge = null, badgeColor = "teal" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const badgeColors = {
    teal:   "bg-teal-100 text-teal-700",
    green:  "bg-green-100 text-green-700",
    red:    "bg-red-100 text-red-700",
    yellow: "bg-yellow-100 text-yellow-700",
  };
  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 ${isOpen ? "border-b border-gray-200" : ""}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Icon className="w-4 h-4 text-teal-600" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{title}</span>
              {badge && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badgeColors[badgeColor]}`}>{badge}</span>
              )}
            </div>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
};

/* ---------------- Info Card ---------------- */
const InfoCard = ({ icon: Icon, title, value, color = "teal" }) => {
  const colors = {
    teal:  "bg-teal-50/70 border-teal-200 text-teal-700",
    blue:  "bg-blue-50/70 border-blue-200 text-blue-700",
    green: "bg-green-50/70 border-green-200 text-green-700",
    amber: "bg-amber-50/70 border-amber-200 text-amber-700",
  };
  return (
    <div className={`px-3 py-2 rounded-md border ${colors[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] font-medium opacity-70 uppercase tracking-wide">{title}</p>
          <p className="text-sm font-semibold truncate">{value}</p>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Read-only field ---------------- */
const ReadOnlyField = ({ label, value }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">{value}</div>
  </div>
);

/* ---------------- Main Component ---------------- */
export default function LoyaltyProgramConfig({ renderActions }) {
  const { hasPermission } = useAuth();

  const canView = hasPermission(P.CRM.LOYALTY.SCREEN);
  const canEdit = hasPermission(P.CRM.LOYALTY.EDIT);

  const { config, loading, saving, error, fetchConfig, saveConfig } = useLoyalty();
  const [local, setLocal] = useState(null);

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  useEffect(() => {
    if (config) setLocal(config);
  }, [config]);

  // ─── SINGLE renderActions effect ────────────────────────────────────────────
  // Always runs — no early returns before it — so the parent header always gets
  // told what buttons to show. When canView is false we send null for everything
  // so the parent hides "Save Changes" and any other buttons immediately.
  // Previously the effect had `if (!local) return` which caused it to silently
  // do nothing on access-denied (local is never set when canView is false),
  // leaving stale buttons in the parent header.
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!renderActions) return;

    if (!canView) {
      // Access denied — remove every button from the parent header
      renderActions({ onSave: null, onRefresh: null });
      return;
    }

    if (!local) {
      // Still loading — keep existing buttons but don't wire up stale handlers
      return;
    }

    renderActions({
      onSave: canEdit
        ? async () => {
            try {
              await saveConfig(local);
              showToast("Loyalty program updated successfully", "success");
            } catch (err) {
              console.error("Save loyalty config failed:", err);
              showToast(err?.message || "Failed to update loyalty program", "error");
            }
          }
        : null,
      onRefresh: fetchConfig,
    });
  }, [renderActions, local, saveConfig, fetchConfig, canView, canEdit]);

  // ─── Access denied — rendered AFTER all hooks ────────────────────────────────
  if (!canView) {
    return (
     <div className="min-h-full bg-gray-50 px-4 py-6 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
          <p className="text-sm text-gray-500 mt-1">You don't have permission to view Loyalty Program.</p>
        </div>
      </div>
    );
  }

  if (loading || !local) {
    return (
      <div className="w-full p-8 flex items-center justify-center">
        <div className="text-gray-500">Loading loyalty configuration...</div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="w-full p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          Failed to load loyalty program. {error.message}
        </div>
      </div>
    );
  }

  const update = (patch) => setLocal((prev) => ({ ...prev, ...patch }));

  const exampleSpend  = 1000;
  const pointsEarned  = Math.floor(exampleSpend / (local.earnRate || 1));
  const pointsValue   = pointsEarned * (local.redeemRate || 0);

  return (
    <div className="w-full space-y-4">

      {/* ================= PROGRAM STATUS HEADER ================= */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${local.isEnabled ? "bg-green-100" : "bg-gray-100"}`}>
              {local.isEnabled
                ? <CheckCircle className="w-5 h-5 text-green-600" />
                : <AlertTriangle className="w-5 h-5 text-gray-400" />
              }
            </div>
            <div>
              <div className="flex items-center gap-2">
                {canEdit ? (
                  <input
                    value={local.programName}
                    onChange={(e) => update({ programName: e.target.value })}
                    placeholder="Program Name"
                    className="text-base font-semibold text-gray-900 bg-transparent border-none p-0 focus:ring-0 focus:outline-none"
                    style={{ width: `${Math.max(local.programName?.length || 10, 10)}ch` }}
                  />
                ) : (
                  <span className="text-base font-semibold text-gray-900">{local.programName}</span>
                )}
              </div>
              <p className={`text-xs ${local.isEnabled ? "text-green-600" : "text-gray-500"}`}>
                {local.isEnabled ? "Active" : "Disabled"}
              </p>
            </div>
          </div>

          {/* Enable/disable toggle — editors only */}
          {canEdit && (
            <button
              type="button"
              onClick={() => update({ isEnabled: !local.isEnabled })}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${local.isEnabled ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition-transform ${local.isEnabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          )}
        </div>

        {local.isEnabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <InfoCard icon={Coins}     title="Earn Rate"    value={`₹${local.earnRate} = 1 pt`}        color="teal"  />
            <InfoCard icon={Gift}      title="Redeem Value" value={`1 pt = ₹${local.redeemRate}`}       color="blue"  />
            <InfoCard icon={TrendingUp}title="Min. Redeem"  value={`${local.minRedeemPoints} pts`}      color="green" />
            <InfoCard icon={Clock}     title="Expiry"       value={local.pointsExpiryMonths ? `${local.pointsExpiryMonths}mo` : "Never"} color="amber" />
          </div>
        )}
      </div>

      {/* ================= PROGRAM SETTINGS ================= */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Left: Earning & Redemption */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <Coins className="w-3.5 h-3.5" /> Earning &amp; Redemption
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {canEdit ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Earn Rate</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input type="number" min="1" value={local.earnRate}
                        onChange={(e) => update({ earnRate: Number(e.target.value) })}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">per 1 point</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Redeem Value</label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                      <input type="number" min="0.1" step="0.1" value={local.redeemRate}
                        onChange={(e) => update({ redeemRate: Number(e.target.value) })}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">per 1 point</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Min. Redeem</label>
                    <input type="number" min="1" value={local.minRedeemPoints}
                      onChange={(e) => update({ minRedeemPoints: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">points required</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Points Expiry</label>
                    <select value={local.pointsExpiryMonths || 0}
                      onChange={(e) => update({ pointsExpiryMonths: Number(e.target.value) || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    >
                      <option value={0}>Never</option>
                      <option value={3}>3 months</option>
                      <option value={6}>6 months</option>
                      <option value={12}>12 months</option>
                      <option value={24}>24 months</option>
                    </select>
                    <p className="text-[10px] text-gray-400 mt-0.5">after inactivity</p>
                  </div>
                </>
              ) : (
                <>
                  <ReadOnlyField label="Earn Rate"     value={`₹${local.earnRate} per point`} />
                  <ReadOnlyField label="Redeem Value"  value={`₹${local.redeemRate} per point`} />
                  <ReadOnlyField label="Min. Redeem"   value={`${local.minRedeemPoints} points`} />
                  <ReadOnlyField label="Points Expiry" value={local.pointsExpiryMonths ? `${local.pointsExpiryMonths} months` : "Never"} />
                </>
              )}
            </div>
            <div className="px-3 py-2 bg-teal-50/50 rounded-md border border-teal-100 flex items-center gap-2">
              <Calculator className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
              <p className="text-[11px] text-teal-700">
                ₹{exampleSpend} → {pointsEarned} pts → ₹{pointsValue}
                <span className="text-teal-500 ml-1">({((pointsValue / exampleSpend) * 100).toFixed(1)}%)</span>
              </p>
            </div>
          </div>

          {/* Right: Quick Info */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Quick Info
            </h4>
            <div className="space-y-2">
              {[
                { label: "Spend for 1 point",      value: `₹${local.earnRate}` },
                { label: "1 point value",           value: `₹${local.redeemRate}` },
                { label: "Min. redeem threshold",   value: `${local.minRedeemPoints} pts (₹${local.minRedeemPoints * local.redeemRate})` },
                { label: "Spend to first redeem",   value: `₹${local.minRedeemPoints * local.earnRate}` },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                  <span className="text-xs text-gray-600">{label}</span>
                  <span className="text-xs font-semibold text-gray-900">{value}</span>
                </div>
              ))}
              <div className={`flex items-center justify-between px-3 py-2 rounded-md ${local.pointsExpiryMonths ? "bg-amber-50" : "bg-green-50"}`}>
                <span className="text-xs text-gray-600">Points expiry</span>
                <span className={`text-xs font-semibold ${local.pointsExpiryMonths ? "text-amber-700" : "text-green-700"}`}>
                  {local.pointsExpiryMonths ? `${local.pointsExpiryMonths} months` : "Never expires"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= REFERRAL PROGRAM ================= */}
      <Section
        title="Referral Program"
        subtitle="Reward customers for referrals"
        icon={UserPlus}
        badge={local.referralEnabled ? "Active" : "Off"}
        badgeColor={local.referralEnabled ? "green" : "red"}
      >
        <div className="space-y-4">
          {canEdit ? (
            <>
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                <div>
                  <p className="text-sm font-medium text-gray-900">Enable Referral</p>
                  <p className="text-[10px] text-gray-500">Reward both parties on successful referral</p>
                </div>
                <button
                  type="button"
                  onClick={() => update({ referralEnabled: !local.referralEnabled })}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${local.referralEnabled ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 bg-white rounded-full shadow transform transition-transform ${local.referralEnabled ? "translate-x-5" : "translate-x-1"}`} />
                </button>
              </div>
              {local.referralEnabled && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Referrer Bonus</label>
                      <input type="number" min="0" value={local.referrerBonus}
                        onChange={(e) => update({ referrerBonus: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">{local.referrerBonus} pts = ₹{local.referrerBonus * local.redeemRate}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">New Customer Bonus</label>
                      <input type="number" min="0" value={local.refereeBonus}
                        onChange={(e) => update({ refereeBonus: Number(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">{local.refereeBonus} pts = ₹{local.refereeBonus * local.redeemRate}</p>
                    </div>
                  </div>
                  <div className="px-3 py-2 bg-purple-50/50 rounded-md border border-purple-100 flex items-center gap-2">
                    <Award className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                    <p className="text-[11px] text-purple-700">
                      Cost per referral: ₹{(local.referrerBonus + local.refereeBonus) * local.redeemRate}
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                <span className="text-xs text-gray-600">Status</span>
                <span className={`text-xs font-semibold ${local.referralEnabled ? "text-green-700" : "text-gray-500"}`}>
                  {local.referralEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              {local.referralEnabled && (
                <>
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-600">Referrer Bonus</span>
                    <span className="text-xs font-semibold text-gray-900">{local.referrerBonus} pts</span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-600">New Customer Bonus</span>
                    <span className="text-xs font-semibold text-gray-900">{local.refereeBonus} pts</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}